import React, { ReactNode } from 'react';

/**
 * Wraps string children in spans to prevent Google Translate crashes.
 *
 * Google Translate replaces text nodes with <font> elements. If React tries to
 * update those nodes, it crashes with "Failed to execute 'removeChild'".
 *
 * This recursive approach is needed because some consumers pass mixed content:
 *   <HBButton><Icon /> {t('label')}</HBButton>
 * A simple wrapper leaves bare text nodes vulnerable. This ensures every string
 * gets its own span. Critical for checkout flows where crashes lose payments.
 *
 * @see https://honeybook.atlassian.net/browse/CORE-5428
 */
export function recursivelyWrapStringChildrenWithSpans(
    _children: ReactNode
): ReactNode {
    return React.Children.toArray(_children).map(child => {
        if (typeof child === 'string') {
            return <span key={child}>{child}</span>;
        } else if (React.isValidElement(child)) {
            return React.cloneElement(
                child,
                undefined,
                recursivelyWrapStringChildrenWithSpans(child.props.children)
            );
        }
        return child;
    });
}
